import React, { useEffect, useMemo, useState } from 'react';
import CentralCalls from '../centralCalls';
import { makeStyles } from '@mui/styles';
import {
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Chip,
    Divider,
    Paper,
    IconButton,
    Tooltip,
    Box,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faExternalLinkAlt, faSyncAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Auth } from 'aws-amplify';

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
    root: {
        fontFamily: 'Arial, sans-serif',
        margin: theme.spacing(2),
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: `${drawerWidth}px 1fr`,
        gap: theme.spacing(2),
        minHeight: '80vh',
    },
    sidebar: {
        padding: theme.spacing(2),
        borderRadius: 12,
    },
    main: {
        padding: theme.spacing(2),
        borderRadius: 12,

        overflow: 'hidden'
    },
    sectionTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
    },
    filtersRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr', // updated to add Priority filter column
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    searchBox: {
        marginBottom: theme.spacing(1),
    },
    listContainer: {
        maxHeight: 'calc(80vh - 220px)',
        overflowY: 'auto',
        marginTop: theme.spacing(1),
        paddingRight: 4,
    },
    listItem: {
        padding: theme.spacing(1.25),
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background 0.18s ease',
        '&:hover': {
            background: '#f7f7f7',
        },
        '&.active': {
            outline: '2px solid #1976d2',
            background: '#f0f7ff',
        },
    },
    listItemTop: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    smallMuted: {
        fontSize: '0.75rem',
        color: '#666',
    },
    descriptionClamp: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize: '.9rem',
    },
    statusChip: {
        height: 22,
        fontSize: 12,
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    grow: { flex: 1 },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '100px 100px', // 2 narrow columns for hours
        gap: 8,
        alignItems: 'center',

        // children sizing
        '& .MuiTextField-root': {
            width: '100%',
        },
    },
    fieldRowStacked: {
        marginTop: 8,
        '& .MuiTextField-root': {
            width: '210px',  // keep Created a bit wider
        },
    },
    commentBlock: {
        marginTop: theme.spacing(2),
    },
    commentList: {
        marginTop: theme.spacing(1),
        paddingLeft: theme.spacing(2),
    },
    commentItem: {
        padding: theme.spacing(0.75, 1),
        borderRadius: 8,
        background: '#fafafa',
        '& + &': { marginTop: 6 },
    },
    addCommentRow: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    formPanel: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderRadius: 10,
        background: '#fafafa',
    },
    detailsGrid: {
        display: 'grid',
        padding: '8px',
        gridTemplateColumns: '75% 25%',
        gap: theme.spacing(2),
        alignItems: 'stretch',
        // fixed visual height so both columns match
        maxHeight: '60vh',
        // allow children to shrink so scrolling can happen
        '& > *': { minHeight: 0 },
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
            maxHeight: 'unset',
        },
    },

    descArea: {
        gridColumn: '1 / 2',
        // span full height of the right column
        gridRow: '1 / -1',
        minWidth: 0,
        overflow: 'visible',    // <-- scroll inside left column
        position: 'relative',     // for corner edit button
        display: 'flex',
        flexDirection: 'column',

    },

    descContent: {
        // a read-mode container that scrolls within descArea
        whiteSpace: 'pre-wrap',
        lineHeight: 1.35,
        paddingRight: theme.spacing(4), // leave room for the corner icon
    },

    rightCol: {
        gridColumn: '2 / 3',
        display: 'grid',
        gridTemplateRows: 'auto auto auto', // Status, Hours, Created
        rowGap: theme.spacing(2),
        minWidth: 0,
        [theme.breakpoints.down('md')]: {
            gridColumn: '1 / 2',
        },
    },

    hoursRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',   // split equally
        columnGap: theme.spacing(1),
        padding: '0px',
        margin: '0px',
        minWidth: 0,
        '& .MuiTextField-root': { width: '100%' },
    },

    createdRow: {
        '& .MuiTextField-root': { width: '100%' },
    },

    // tiny corner buttons for edit/save
    cornerIconBtn: {
        position: 'absolute',
        top: 6,
        right: 6,
        padding: 4,
        fontSize: 12,
        lineHeight: 1,
        opacity: 0.8,
        '&:hover': { opacity: 1 },
    },

    // small inline icon inside textfields (hours)
    adornmentIconBtn: {
        padding: 4,
        fontSize: 12,
        lineHeight: 1,
    },

}));

/** Sidebar category links */
const categoryLinks = [
    { category: 'All Phases', relativeLink: '/phase/slab' },
    { category: 'Slab', relativeLink: '/phase/slab' },
    { category: 'Rough', relativeLink: '/phase/rough' },
    { category: 'Underground', relativeLink: '/phase/underground' },
    { category: 'Draintubs', relativeLink: '/phase/draintubs' },
    { category: 'Setout', relativeLink: '/phase/setout' },
    { category: 'Warranty', relativeLink: '/phase/warranty' },
    { category: 'Quality Check', relativeLink: '/phase/slab_qc' },
    { category: 'WIP', relativeLink: '/wip' },
    { category: 'PO', relativeLink: '/phase/po' },
    { category: 'Permit', relativeLink: '/phase/permit' },
    { category: 'Lot', relativeLink: '/phase/lot' },
    { category: 'Menu', relativeLink: '/menu' },
    { category: 'Style/Color', relativeLink: '/style-color' },
    { category: 'Schedule', relativeLink: '/dailySchedule' },
    { category: 'Daily Schedule', relativeLink: '/dailySchedule' },
    { category: 'Employee Schedule', relativeLink: '/employeeSchedule' },
    { category: 'Reports', relativeLink: '/reports' },
    { category: 'Piece Work' },
    { category: 'Materials', relativeLink: '/wip' },
    { category: 'Excel Import/Export', relativeLink: '/export' },
    { category: 'Other' },
];

const CATEGORY_OPTIONS = [
    'All Phases',
    'Slab',
    'Rough',
    'Underground',
    'Draintubs',
    'Setout',
    'Warranty',
    'Quality Check',
    'WIP',
    'PO',
    'Permit',
    'Lot',
    'Menu',
    'Style/Color',
    'Schedule',
    'Daily Schedule',
    'Materials',
    'Employee Schedule',
    'Admin Tables',
    'Lot Filter Selector',
    'Reports',
    'Piece Work',
    'Excel Import/Export',
    'Other',
];

const linkForCategory = (cat) =>
    categoryLinks.find((c) => c.category === cat && c.relativeLink)?.relativeLink || null;

function WorkItems() {
    const classes = useStyles();
    const [newTaskOpen, setNewTaskOpen] = useState(false);
    const [newTaskDraft, setNewTaskDraft] = useState({
        category: 'All Phases',
        description: '',
        name: '',
    });
    const openNewTask = () => {
        setNewTaskDraft({ category: 'All Phases', description: '', name: '' });
        setNewTaskOpen(true);
    };
    const closeNewTask = () => setNewTaskOpen(false);

    const [tasks, setTasks] = useState([]);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [userName, setUserName] = React.useState('');
    // Filters
    const [statusFilter, setStatusFilter] = useState(''); // '', 'open', ...
    const [categoryFilter, setCategoryFilter] = useState(''); // '', 'Slab', ...
    const [priorityFilter, setPriorityFilter] = useState(''); // '', 'High', 'Mid', 'Low'
    const [search, setSearch] = useState('');
    // field edit toggles
    const [editing, setEditing] = useState({
        description: false,
        est: false,
        act: false,
        category: false,
    });

    const toggleEdit = (key) => setEditing((e) => ({ ...e, [key]: !e[key] }));

    // (newTaskDraft defined above to keep typing local to dialog)
    useEffect(() => {
        async function setUserInfo() {
            try {
                const user = await Auth.currentAuthenticatedUser();
                setUserName(user.attributes.email);
            } catch (error) {
                console.error('Error fetching user info', error);
            }
        }
        setUserInfo();
    }, []);
    useEffect(() => {
        fetchTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handlePriorityChange = async (taskId, newPriority) => {
        try {
            await CentralCalls.upsertData({ priority: newPriority, oid: taskId }, 'task', 'update');
            setTasks((prev) =>
                prev.map((t) => (t.oid === taskId ? { ...t, priority: newPriority } : t))
            );
        } catch (e) {
            console.error('Error updating task priority:', e);
        }
    };

    const handleCategoryChange = async (taskId, newCategory) => {
        try {
            await CentralCalls.upsertData({ category: newCategory, oid: taskId }, 'task', 'update');
            setTasks((prev) =>
                prev.map((t) => (t.oid === taskId ? { ...t, category: newCategory } : t))
            );
        } catch (e) {
            console.error('Error updating task category:', e);
        }
    };

    const fetchTasks = async () => {
        try {
            const fetched = await CentralCalls.callStoredProcedureWithParams('ui_sp_getTaskData', {});
            console.log(fetched);
            const initialized =
                fetched?.map((t) => ({
                    ...t,
                    editedDescription: t.description,
                    editedEstimatedHours: t.estimated_hours ?? '',
                    editedActualHours: t.actual_hours ?? '',
                })) ?? [];

            // sort: active first, then by date desc
            // const weight = (t) =>
            //     t.status === 'complete' ? 2 : t.status === 'rework' ? 1 : 0; // open/testing first

            // initialized.sort((a, b) => {
            //     const w = weight(a) - weight(b);
            //     if (w !== 0) return w;
            //     return new Date(b.date).getTime() - new Date(a.date).getTime();
            // });

            initialized.forEach((t) => {
                const taskMs = t.date ? new Date(t.date).getTime() : 0;
                const latestCommentMs = (t.comments ?? []).reduce((max, c) => {
                    const ms = c?.create_date ? new Date(c.create_date).getTime() : 0;
                    return ms > max ? ms : max;
                }, 0);
                t.lastActivity = Math.max(taskMs, latestCommentMs);
            });

            // sort by latest activity (desc). If you still want your status weighting, apply it SECONDARY.
            initialized.sort((a, b) => b.lastActivity - a.lastActivity);

            setTasks(initialized);
            if (!selectedTaskId && initialized.length > 0) setSelectedTaskId(initialized[0].oid);
        } catch (e) {
            console.error('Error fetching tasks:', e);
        }
    };

    // Derived lists
    const filteredTasks = useMemo(() => {
        const s = search.trim().toLowerCase();
        return tasks.filter((t) => {
            if (statusFilter && t.status !== statusFilter) return false;
            if (categoryFilter && t.category !== categoryFilter) return false;
            if (priorityFilter && ((t.priority ?? '') !== priorityFilter)) return false;
            if (s) {
                const hay = `${t.description ?? ''} ${t.category ?? ''}`.toLowerCase();
                return hay.includes(s);
            }
            return true;
        });
    }, [tasks, statusFilter, categoryFilter, priorityFilter, search]);

    const selectedTask = useMemo(
        () => tasks.find((t) => t.oid === selectedTaskId) || null,
        [tasks, selectedTaskId]
    );
    const saveDescription = async () => {
        if (!selectedTask) return;
        const payload = {
            oid: selectedTask.oid,
            description: (selectedTask.editedDescription || '').trim(),
        };
        try {
            await CentralCalls.upsertData(payload, 'task', 'update');
            await fetchTasks();
            setEditing((e) => ({ ...e, description: false }));
        } catch (e) {
            console.error('Error saving description:', e);
        }
    };

    const saveHours = async (field /* 'estimated_hours' | 'actual_hours' */) => {
        if (!selectedTask) return;
        const val =
            field === 'estimated_hours'
                ? selectedTask.editedEstimatedHours
                : selectedTask.editedActualHours;

        const payload = {
            oid: selectedTask.oid,
            [field]: val === '' || val === null ? null : Number(val),
        };
        try {
            await CentralCalls.upsertData(payload, 'task', 'update');
            await fetchTasks();
            setEditing((e) => ({
                ...e,
                est: field === 'estimated_hours' ? false : e.est,
                act: field === 'actual_hours' ? false : e.act,
            }));
        } catch (e) {
            console.error('Error saving hours:', e);
        }
    };

    // Actions
    const handleStatusChange = async (taskId, newStatus) => {
        try {
            await CentralCalls.upsertData({ status: newStatus, oid: taskId }, 'task', 'update');
            setTasks((prev) => prev.map((t) => (t.oid === taskId ? { ...t, status: newStatus } : t)));
        } catch (e) {
            console.error('Error updating task status:', e);
        }
    };

    const handleSaveSelected = async () => {
        if (!selectedTask) return;
        const payload = {
            oid: selectedTask.oid,
            description: (selectedTask.editedDescription || '').trim(),
            estimated_hours:
                selectedTask.editedEstimatedHours === '' ? null : Number(selectedTask.editedEstimatedHours),
            actual_hours:
                selectedTask.editedActualHours === '' ? null : Number(selectedTask.editedActualHours),
        };
        if (!payload.description) return;

        try {
            await CentralCalls.upsertData(payload, 'task', 'update');
            await fetchTasks();
        } catch (e) {
            console.error('Error saving task:', e);
        }
    };

    const handleAddTask = async () => {
        if (!newTaskDraft.description.trim()) return;
        try {
            await CentralCalls.upsertData(newTaskDraft, 'task', 'insert');
            setNewTaskDraft({ category: 'All Phases', description: '', name: '' });
            closeNewTask();
            await fetchTasks();
        } catch (e) {
            console.error('Error adding task:', e);
        }
    };


    const handleAddComment = async () => {
        if (!selectedTask?.newComment?.trim()) return;
        try {
            await CentralCalls.upsertData(
                { task_oid: selectedTask.oid, comment: selectedTask.newComment.trim(), user_email: userName },
                'task_comment',
                'insert'
            );
            await fetchTasks();
            setTasks((prev) =>
                prev.map((t) => (t.oid === selectedTask.oid ? { ...t, newComment: '' } : t))
            );
        } catch (e) {
            console.error('Error adding comment:', e);
        }
    };

    const baseUrl = window.location.origin;

    return (
        <div className={classes.root}>
            {/* Header */}
            <div className={classes.headerRow}>
                <Typography variant="h5" className={classes.grow}>
                    Work Items
                </Typography>

                <Tooltip title="Refresh">
                    <IconButton onClick={fetchTasks}>
                        <FontAwesomeIcon icon={faSyncAlt} />
                    </IconButton>
                </Tooltip>
            </div>

            <div className={classes.layout}>
                {/* Sidebar */}
                <Paper elevation={1} className={classes.sidebar}>


                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography className={classes.sectionTitle} variant="subtitle1">
                            Tasks
                        </Typography>
                        <Tooltip title="New Task">
                            <IconButton size="small" onClick={openNewTask}>
                                <FontAwesomeIcon icon={faPlus} />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className={classes.filtersRow}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="open">Open</MenuItem>
                                <MenuItem value="testing">Ready to Test</MenuItem>
                                <MenuItem value="rework">Rework</MenuItem>
                                <MenuItem value="complete">Complete</MenuItem>
                                <MenuItem value="paid">Closed (paid)</MenuItem>
                                <MenuItem value="closed">Closed (Not Complete)</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                label="Category"
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {CATEGORY_OPTIONS.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                label="Priority"
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Mid">Mid</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <TextField
                        className={classes.searchBox}
                        size="small"
                        label="Search"
                        placeholder="Search description…"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <div className={classes.listContainer}>
                        {filteredTasks.map((t) => {
                            const active = t.oid === selectedTaskId;
                            const link = linkForCategory(t.category);
                            return (
                                <div
                                    key={t.oid}
                                    className={`${classes.listItem} ${active ? 'active' : ''}`}
                                    onClick={() => setSelectedTaskId(t.oid)}
                                >
                                    <div className={classes.listItemTop}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chip
                                                className={classes.statusChip}
                                                label={
                                                    t.status === 'testing'
                                                        ? 'Ready to Test'
                                                        : t.status.charAt(0).toUpperCase() + t.status.slice(1)
                                                }
                                                variant="outlined"
                                            />
                                            <Typography className={classes.smallMuted}>
                                                {new Date(t.date).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: '2-digit',
                                                })}
                                            </Typography>
                                        </Box>
                                        {link && (
                                            <Tooltip title={`Open ${t.category}`}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(`${window.location.origin}${link}`, '_blank');
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                        {t.category}
                                    </Typography>
                                    <Typography className={classes.descriptionClamp}>{t.description}</Typography>
                                    <Typography className={classes.smallMuted} style={{ marginTop: 4 }}>
                                        Est: {t.estimated_hours ?? '-'}h • Act: {t.actual_hours ?? '-'}h
                                    </Typography>
                                </div>
                            );
                        })}
                        {filteredTasks.length === 0 && (
                            <Typography className={classes.smallMuted}>No tasks match your filters.</Typography>
                        )}
                    </div>
                </Paper>

                {/* Main Content */}
                <Paper elevation={1} className={classes.main}>
                    {!selectedTask ? (
                        <Typography color="textSecondary">Select a task to view details.</Typography>
                    ) : (
                        <>
                            <div className={classes.headerRow}>
                                <Box display="flex" alignItems="center" className={classes.grow}>
                                    {!editing.category ? (
                                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                                            {selectedTask.category}
                                        </Typography>
                                    ) : (
                                        <FormControl size="small" variant="outlined">
                                            <InputLabel shrink>Category</InputLabel>
                                            <Select
                                                value={selectedTask.category ?? ''}
                                                onChange={(e) => {
                                                    handleCategoryChange(selectedTask.oid, e.target.value);
                                                    setEditing((s) => ({ ...s, category: false }));
                                                }}
                                                autoFocus
                                            >
                                                {CATEGORY_OPTIONS.map((c) => (
                                                    <MenuItem key={c} value={c}>
                                                        {c}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <Tooltip title="Edit category">
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleEdit('category')}
                                            style={{ marginLeft: 8 }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {linkForCategory(selectedTask.category) && (
                                    <Tooltip title={`Open ${selectedTask.category} page`}>
                                        <IconButton
                                            onClick={() =>
                                                window.open(
                                                    `${baseUrl}${linkForCategory(selectedTask.category)}`,
                                                    '_blank'
                                                )
                                            }
                                        >
                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FontAwesomeIcon icon={faSave} />}
                                    onClick={handleSaveSelected}
                                >
                                    Save
                                </Button>
                            </div>

                            <div className={classes.detailsGrid}>
                                {/* LEFT: Description (scrolls, spans full right height) */}
                                <div className={classes.descArea}>
                                    {!editing.description ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={5}
                                            variant="outlined"
                                            value={selectedTask.editedDescription ?? ''}
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                            label={
                                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                    <span>Description</span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleEdit('description')}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            }
                                        />
                                    ) : (
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={5}
                                            variant="outlined"
                                            value={selectedTask.editedDescription ?? ''}
                                            onChange={(e) =>
                                                setTasks((prev) =>
                                                    prev.map((t) =>
                                                        t.oid === selectedTask.oid ? { ...t, editedDescription: e.target.value } : t
                                                    )
                                                )
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                    <span>Description</span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={saveDescription}
                                                    >
                                                        <FontAwesomeIcon icon={faSave} fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            }
                                        />
                                    )}
                                </div>


                                {/* RIGHT: Status, Hours, Created */}
                                <div className={classes.rightCol}>
                                    {/* category moved to header; right column no longer renders category */}

                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            label="Status"
                                            value={selectedTask.status}
                                            onChange={(e) => handleStatusChange(selectedTask.oid, e.target.value)}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="open">Open</MenuItem>
                                            <MenuItem value="testing">Ready to Test</MenuItem>
                                            <MenuItem value="rework">Rework</MenuItem>
                                            <MenuItem value="complete">Complete</MenuItem>
                                            <MenuItem value="paid">Closed (paid)</MenuItem>
                                            <MenuItem value="closed">Closed (Not Complete)</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" fullWidth style={{ marginTop: 8 }}>
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            label="Priority"
                                            value={selectedTask.priority ?? ''}
                                            onChange={(e) => handlePriorityChange(selectedTask.oid, e.target.value)}
                                        >
                                            <MenuItem value="High">High</MenuItem>
                                            <MenuItem value="Mid">Mid</MenuItem>
                                            <MenuItem value="Low">Low</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {/* Hours row: Est + Act split equally; tiny edit/save inside */}
                                    <div className={classes.hoursRow}>
                                        {/* Estimated Hours */}
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={selectedTask.editedEstimatedHours ?? ''}
                                            onChange={(e) =>
                                                setTasks((prev) =>
                                                    prev.map((t) =>
                                                        t.oid === selectedTask.oid ? { ...t, editedEstimatedHours: e.target.value } : t
                                                    )
                                                )
                                            }
                                            InputProps={{ readOnly: !editing.est }}
                                            InputLabelProps={{ shrink: true }}
                                            label={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    lineHeight={1}
                                                    fontSize="0.75rem"
                                                    width="100%"
                                                >
                                                    <span>Est. Hrs</span>
                                                    <IconButton
                                                        size="small"
                                                        style={{ padding: 2 }}
                                                        onClick={() => (editing.est ? saveHours('estimated_hours') : toggleEdit('est'))}
                                                        aria-label={editing.est ? 'Save estimated hours' : 'Edit estimated hours'}
                                                    >
                                                        <FontAwesomeIcon icon={editing.est ? faSave : faEdit} />
                                                    </IconButton>
                                                </Box>
                                            }
                                        />

                                        {/* Actual Hours */}
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={selectedTask.editedActualHours ?? ''}
                                            onChange={(e) =>
                                                setTasks((prev) =>
                                                    prev.map((t) =>
                                                        t.oid === selectedTask.oid ? { ...t, editedActualHours: e.target.value } : t
                                                    )
                                                )
                                            }
                                            InputProps={{ readOnly: !editing.act }}
                                            InputLabelProps={{ shrink: true }}
                                            label={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    lineHeight={1}
                                                    fontSize="0.75rem"
                                                    width="100%"
                                                >
                                                    <span>Act. Hrs</span>
                                                    <IconButton
                                                        size="small"
                                                        style={{ padding: 2 }}
                                                        onClick={() => (editing.act ? saveHours('actual_hours') : toggleEdit('act'))}
                                                        aria-label={editing.act ? 'Save actual hours' : 'Edit actual hours'}
                                                    >
                                                        <FontAwesomeIcon icon={editing.act ? faSave : faEdit} />
                                                    </IconButton>
                                                </Box>
                                            }
                                        />
                                    </div>


                                    <div className={classes.createdRow}>
                                        <TextField
                                            label="Created"
                                            size="small"
                                            value={new Date(selectedTask.date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: '2-digit',
                                            })}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </div>
                                </div>
                            </div>


                            <Divider style={{ margin: '16px 0' }} />

                            {/* Comments */}
                            <div className={classes.commentBlock}>
                                <Typography variant="subtitle1" className={classes.sectionTitle}>
                                    Comments
                                </Typography>
                                <div className={classes.addCommentRow}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Add a comment…"
                                        value={selectedTask.newComment ?? ''}
                                        onChange={(e) =>
                                            setTasks((prev) =>
                                                prev.map((t) =>
                                                    t.oid === selectedTask.oid ? { ...t, newComment: e.target.value } : t
                                                )
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddComment();
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={handleAddComment}
                                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                                    >
                                        Add
                                    </Button>
                                </div>

                                <div className={classes.commentList}>
                                    {selectedTask.comments?.length ? (
                                        selectedTask.comments.map((c) => (
                                            <div key={c.comment_oid} className={classes.commentItem}>
                                                <Typography variant="body2">{c.comment}</Typography>
                                                <Typography variant="caption" className={classes.smallMuted}>
                                                    {c.user_email ? `${c.user_email}` : 'Unknown User'} •{' '}
                                                    {c.create_date
                                                        ? new Date(c.create_date).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: '2-digit',
                                                        })
                                                        : ''}
                                                </Typography>
                                            </div>
                                        ))
                                    ) : (
                                        <Typography className={classes.smallMuted}>No comments yet.</Typography>
                                    )}
                                </div>

                            </div>
                        </>
                    )}
                </Paper>
            </div>

            <Dialog open={newTaskOpen} onClose={closeNewTask} fullWidth maxWidth="sm">
                <DialogTitle>New Task</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12}>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    label="Category"
                                    value={newTaskDraft.category}
                                    onChange={(e) => setNewTaskDraft((p) => ({ ...p, category: e.target.value }))}
                                >
                                    {CATEGORY_OPTIONS.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={newTaskDraft.description}
                                onChange={(e) => setNewTaskDraft((p) => ({ ...p, description: e.target.value }))}
                                multiline
                                minRows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeNewTask}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleAddTask}>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
                        Add Task
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}

export default WorkItems;
